/**
 * A call's story from its events (design 09 §4, design 11 §3.3): each event becomes an entry at its
 * time from the call's start, with a title, details and a tone. A tool call and its result are one
 * entry; a turn is coloured against the latency targets (ADR-0003: end of speech to the agent's
 * voice, p50 under 1.0 s, p95 under 1.6 s). An event this dashboard does not know yet is shown by
 * its type rather than dropped.
 */
import type { MessageId } from "../../i18n";

export interface CallEvent {
  id: string;
  type: string;
  time?: string | null;
  swaraloseq: number;
  data: Record<string, unknown>;
}

export type Tone = "ok" | "warn" | "bad" | "info";

export interface Entry {
  id: string;
  offsetMs: number;
  tone: Tone;
  title: { id: MessageId; values?: Record<string, string | number> } | { raw: string };
  details: string[];
  /** What a search_knowledge call found and the agent may have cited (E07). */
  sources?: Source[];
}

export interface Source {
  chunkId: string;
  documentId: string;
  title: string;
  score: number;
  snippet: string;
}

function sourcesOf(v: unknown): Source[] | undefined {
  if (!Array.isArray(v)) return undefined;
  return (v as Record<string, unknown>[]).map((s) => ({
    chunkId: str(s.chunk_id), documentId: str(s.document_id), title: str(s.title), score: Number(s.score ?? 0),
    snippet: str(s.snippet),
  }));
}

/** ADR-0003's targets for a turn's total latency (end of speech to the agent's voice). */
export const TURN_TARGET_MS = 1000; // p50
export const TURN_LIMIT_MS = 1600; // p95

export function turnTone(totalMs: number): Tone {
  return totalMs <= TURN_TARGET_MS ? "ok" : totalMs <= TURN_LIMIT_MS ? "warn" : "bad";
}

const str = (v: unknown) => (v === undefined || v === null ? "" : String(v));

/** Who answered an outbound call (amd.result, in Twilio's AnsweredBy terms). */
const ANSWERED_BY: Record<string, MessageId> = {
  human: "inspect.amd.human", unknown: "inspect.amd.unknown", fax: "inspect.amd.fax",
  machine_start: "inspect.amd.machine_start", machine_end_beep: "inspect.amd.machine_end_beep",
  machine_end_silence: "inspect.amd.machine_end_silence", machine_end_other: "inspect.amd.machine_end_other",
};
/** How a machine's greeting ended (amd.machine_end), so a voicemail could be left. */
const GREETING_END: Record<string, MessageId> = {
  machine_end_beep: "inspect.machineEnd.beep", machine_end_silence: "inspect.machineEnd.silence",
  machine_end_other: "inspect.machineEnd.other",
};
/** A warm transfer's steps (action.progress). */
const TRANSFER_STAGE: Record<string, MessageId> = {
  dialing: "inspect.transfer.dialing", answered: "inspect.transfer.answered",
  whispering: "inspect.transfer.whispering", bridged: "inspect.transfer.bridged",
};
/** A Call Script verb's outcome (script.verb, E08): a failure is bad, running out of time a warning. */
const VERB_TONE: Record<string, Tone> = { failed: "bad", timeout: "warn", interrupted: "info" };
const detailOf = (v: unknown) =>
  v && typeof v === "object" && Object.keys(v).length
    ? Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k}: ${typeof x === "object" ? JSON.stringify(x) : str(x)}`)
    : [];

const ENDED_TONE: Record<string, Tone> = { completed: "ok", transferred: "ok", voicemail: "ok", machine: "warn" };

export function timeline(events: CallEvent[]): Entry[] {
  const ordered = [...events].sort((a, b) => a.swaraloseq - b.swaraloseq);
  const start = ordered.find((e) => e.time)?.time;
  const t0 = start ? Date.parse(start) : 0;
  const offset = (e: CallEvent) => (e.time && t0 ? Date.parse(e.time) - t0 : 0);
  const results = new Map(
    ordered.filter((e) => e.type === "agent.tool.result").map((e) => [str(e.data.tool_call_id), e]),
  );
  const entries: Entry[] = [];
  for (const e of ordered) {
    const d = e.data;
    const at = offset(e);
    const add = (tone: Tone, title: Entry["title"], details: string[] = [], sources?: Source[]) =>
      entries.push({ id: e.id, offsetMs: at, tone, title, details: details.filter(Boolean), ...(sources ? { sources } : {}) });
    switch (e.type) {
      case "call.state_changed":
        add("info", { id: "inspect.state", values: { from: str(d.from_state), to: str(d.to_state) } },
          [str(d.trigger), str(d.reason)]);
        break;
      case "compliance.decision": {
        const outcome = str(d.outcome);
        const hits = (v: unknown) => (Array.isArray(v) ? (v as { rule?: string; detail?: string }[]) : [])
          .map((r) => `${str(r.rule)}: ${str(r.detail)}`);
        const warnings = hits(d.warnings); // advised against, not denied (E05-T04)
        add(outcome === "deny" ? "bad" : outcome === "defer" || warnings.length > 0 ? "warn" : "ok",
          { id: "inspect.compliance", values: { point: str(d.point), outcome } },
          [`${str(d.profile)}@${str(d.version)}`, ...hits(d.reasons), ...warnings]);
        break;
      }
      case "action.requested":
        add("info", { id: "inspect.actionRequested", values: { type: str(d.type), source: str((d.source as { kind?: string })?.kind ?? d.source) } });
        break;
      case "action.completed":
        add("ok", { id: "inspect.actionCompleted", values: { type: str(d.type) } });
        break;
      case "action.failed":
        add("bad", { id: "inspect.actionFailed", values: { type: str(d.type) } }, [str(d.code), str(d.message)]);
        break;
      case "action.progress": {
        const stage = TRANSFER_STAGE[str(d.stage)];
        add("info", stage ? { id: stage } : { raw: `${e.type}: ${str(d.stage)}` });
        break;
      }
      case "amd.result": {
        const by = str(d.answered_by);
        const title = ANSWERED_BY[by];
        const machine = by.startsWith("machine") || by === "fax";
        add(machine ? "warn" : by === "human" ? "ok" : "info", title ? { id: title } : { raw: `${e.type}: ${by}` }, [
          typeof d.confidence === "number" ? `${Math.round(d.confidence * 100)}% sure` : "",
          typeof d.ms === "number" ? `in ${d.ms} ms` : "",
          d.on_machine ? `on a machine: ${str(d.on_machine)}` : "",
        ]);
        break;
      }
      case "amd.machine_end": {
        const title = GREETING_END[str(d.answered_by)];
        add("info", title ? { id: title } : { raw: `${e.type}: ${str(d.answered_by)}` },
          [typeof d.ms === "number" ? `${d.ms} ms after it was detected` : ""]);
        break;
      }
      case "agent.tool.called": {
        const result = results.get(str(d.tool_call_id));
        const rd = result?.data;
        const args = d.arguments && Object.keys(d.arguments as object).length ? JSON.stringify(d.arguments) : "";
        if (!rd) add("info", { id: "inspect.toolRunning", values: { tool: str(d.tool) } }, [args]);
        else if (rd.ok)
          add("ok", { id: "inspect.toolOk", values: { tool: str(d.tool), ms: Number(rd.duration_ms ?? 0) } },
            [args, rd.status ? `HTTP ${str(rd.status)}` : ""], sourcesOf(rd.sources));
        else
          add("bad", { id: "inspect.toolFailed", values: { tool: str(d.tool), error: str(rd.error ?? "error") } },
            [args, rd.status ? `HTTP ${str(rd.status)}` : ""]);
        break;
      }
      case "agent.tool.result":
        break; // with its call, above
      case "provider.fallback":
        add("warn", { id: "inspect.fallback", values: { role: str(d.role), from: str(d.from_provider), to: str(d.to_provider) } },
          [str(d.reason)]);
        break;
      case "agent.turn.completed": {
        const total = Number((d.latency_ms as Record<string, number> | undefined)?.total ?? 0);
        add(turnTone(total), { id: "inspect.turn", values: { turn: Number(d.turn), ms: total } },
          d.interrupted ? ["interrupted"] : []);
        break;
      }
      case "transcript.final":
        break; // the transcript beside the timeline shows every line
      case "call.sip":
        break; // the SIP ladder shows the dialog
      case "call.completed": {
        const hangup = (d.hangup ?? {}) as { reason?: string; sip_code?: number | null; cause?: string };
        add(ENDED_TONE[str(d.status)] ?? "bad",
          { id: "inspect.ended", values: { status: str(d.status) } },
          [str(hangup.reason), hangup.sip_code ? `SIP ${hangup.sip_code}` : "", str(hangup.cause)]);
        break;
      }
      case "script.verb":
        add(VERB_TONE[str(d.outcome)] ?? "ok",
          { id: "inspect.script.verb", values: { verb: str(d.verb), outcome: str(d.outcome), ms: Number(d.duration_ms ?? 0) } },
          [d.label ? `id: ${str(d.label)}` : "", `#${str(d.index)}`, ...detailOf(d.detail)]);
        break;
      case "script.hook":
        if (d.error)
          add("bad", { id: "inspect.script.hookFailed", values: { kind: str(d.kind), error: str(d.error) } },
            [str(d.url), d.status ? `HTTP ${str(d.status)}` : "", `${str(d.attempts)} attempt(s)`]);
        else
          add("ok", { id: "inspect.script.hook", values: { kind: str(d.kind), status: str(d.status ?? "—"), ms: Number(d.duration_ms ?? 0) } },
            [str(d.url), `${str(d.verbs ?? 0)} verb(s)`]);
        break;
      case "script.error": {
        const problems = Array.isArray(d.problems)
          ? (d.problems as Record<string, unknown>[]).map((p) => `verb ${str(p.index)}: ${str(p.path)}: ${str(p.message)}`)
          : [];
        add(d.severity === "warning" ? "warn" : "bad",
          { id: d.severity === "warning" ? "inspect.script.warning" : "inspect.script.error", values: { code: str(d.code) } },
          problems.length ? problems : [str(d.message)]);
        break;
      }
      case "agent.flow.node":
        add("info", { id: "inspect.flowNode", values: { node: str(d.node) } },
          [d.previous ? `from ${str(d.previous)}` : "the first node", d.function ? `by ${str(d.function)}` : ""]);
        break;
      case "agent.handoff":
        add("info", { id: "inspect.handoff", values: { to: str(d.to_agent) } }, [`from ${str(d.from_agent)}`, str(d.reason)]);
        break;
      case "usage.limit.reached":
        add("bad", { id: "inspect.limit" }, [str(d.limit)]);
        break;
      default:
        add("info", { raw: e.type });
    }
  }
  return entries;
}

export interface SipStep {
  id: string;
  offsetMs: number;
  message: string;
  /** By the media node: "received" came from the far end (the carrier, or the caller's phone). */
  direction: "received" | "sent";
  code: number | null;
}

/** The call leg's SIP dialog, in order, from its `call.sip` events. */
export function ladder(events: CallEvent[]): SipStep[] {
  const ordered = [...events].sort((a, b) => a.swaraloseq - b.swaraloseq);
  const t0 = Date.parse(ordered.find((e) => e.time)?.time ?? "") || 0;
  return ordered
    .filter((e) => e.type === "call.sip")
    .map((e) => {
      const at = Date.parse(str(e.data.at) || str(e.time));
      return {
        id: e.id,
        offsetMs: at && t0 ? at - t0 : 0,
        message: str(e.data.message),
        direction: e.data.direction === "sent" ? "sent" : "received",
        code: typeof e.data.code === "number" ? e.data.code : null,
      };
    });
}

/** MOS as FreeSWITCH estimates it (at most 4.5): 4.0 and up is good; under 3.6 people notice. */
export function mosTone(mos: number): Tone {
  return mos >= 4 ? "ok" : mos >= 3.6 ? "warn" : "bad";
}

/** "+1:02.4": an offset from the call's start. */
export function clock(ms: number): string {
  const s = Math.max(0, ms) / 1000;
  const m = Math.floor(s / 60);
  return `+${m}:${(s - m * 60).toFixed(1).padStart(4, "0")}`;
}

/** The stages of a turn, in the order they happen, with their share of the total. */
export const STAGES = ["vad", "turn", "stt", "llm_ttft", "tool", "tts_ttfb"] as const;
export type Stage = (typeof STAGES)[number];

export interface Turn {
  turn: number;
  total: number;
  stages: { stage: Stage; ms: number }[];
  interrupted: boolean;
  offsetMs: number;
}

export function turns(events: CallEvent[]): Turn[] {
  const t0 = Date.parse([...events].sort((a, b) => a.swaraloseq - b.swaraloseq).find((e) => e.time)?.time ?? "") || 0;
  return events
    .filter((e) => e.type === "agent.turn.completed")
    .sort((a, b) => a.swaraloseq - b.swaraloseq)
    .map((e) => {
      const latency = (e.data.latency_ms ?? {}) as Partial<Record<Stage | "total", number>>;
      return {
        turn: Number(e.data.turn),
        total: Number(latency.total ?? 0),
        stages: STAGES.filter((s) => typeof latency[s] === "number").map((s) => ({ stage: s, ms: Number(latency[s]) })),
        interrupted: Boolean(e.data.interrupted),
        offsetMs: e.time && t0 ? Date.parse(e.time) - t0 : 0,
      };
    });
}
